import { ASSETS } from './assets-data';

const termsData = {
  en: {
    seo: {
      title: 'Terms & Conditions | Tropical Marine Construction',
      description: 'Review the Terms and Conditions for Tropical Marine Construction, covering estimates, services, scheduling, payments, warranties, liability, and governing law.',
      keywords: 'Tropical Marine Construction terms, Cape Coral marine contractor terms, dock builder terms, boat lift service agreement'
    },

    heroSection: {
      image: ASSETS.WHATSAPP_IMAGE_2026_05_01_AT_03_25_03_6 || ASSETS.BOATLIFTS_1 || '',
      overlay: 'bg-black/50'
    },

    title: 'Terms & Conditions',
    effectiveDateLabel: 'Effective Date',
    effectiveDate: 'October 1, 2025',

    introParagraphs: [
      'Welcome to Tropical Marine Construction. By accessing our website, requesting an estimate, scheduling a service, approving a proposal, or using our services, you acknowledge that you have read, understood, and agreed to these Terms and Conditions.',
      'These terms constitute a legally binding agreement between you ("Customer") and Tropical Marine Construction ("we," "us," or "our"). If you do not agree to these terms, please do not use our website or services.'
    ],

    sections: [
      {
        id: 'estimates',
        number: 1,
        heading: 'Estimates & Proposals',
        paragraphs: [
          'All estimates and proposals are based on the information available at the time they are prepared. Final pricing may change due to site conditions, permitting requirements, engineering requirements, material changes, labor needs, or customer-requested changes.',
          'A proposal is not considered approved until it is accepted by the customer and any required deposit or written authorization has been received.'
        ]
      },
      {
        id: 'permits',
        number: 2,
        heading: 'Permits & Approvals',
        paragraphs: [
          'Marine construction projects may require city, county, state, federal, HOA, environmental, or other approvals before work can begin.',
          'Project timelines may vary depending on permitting, inspections, agency review times, weather, material availability, and site access.'
        ]
      },
      {
        id: 'payments',
        number: 3,
        heading: 'Payments',
        paragraphs: [
          'Payment terms, deposits, progress payments, and final balances will be outlined in the approved proposal, invoice, or service agreement.',
          'Late payments may delay scheduling, material ordering, inspections, or project completion. Tropical Marine Construction reserves the right to pause work until required payments are received.'
        ]
      },
      {
        id: 'scheduling',
        number: 4,
        heading: 'Scheduling & Delays',
        paragraphs: [
          'We make every reasonable effort to meet scheduled project dates. However, marine construction is affected by weather, tides, permitting, inspections, material availability, equipment scheduling, and site conditions.',
          'Tropical Marine Construction is not responsible for delays caused by events outside our control, including storms, unsafe conditions, agency delays, supply chain issues, or customer-requested changes.'
        ]
      },
      {
        id: 'customer-responsibilities',
        number: 5,
        heading: 'Customer Responsibilities',
        paragraphs: [
          'Customers are responsible for providing accurate project information, access to the property, HOA or owner approvals when applicable, and timely communication regarding project decisions.',
          'Customers must disclose known site issues, utility locations, access limitations, prior structural problems, or other conditions that may affect the work.'
        ]
      },
      {
        id: 'changes',
        number: 6,
        heading: 'Change Orders',
        paragraphs: [
          'Any changes to the approved scope of work may require a written change order and may result in additional costs or timeline adjustments.',
          'Changes may include material upgrades, design modifications, additional repairs, hidden damage, permitting changes, or customer-requested additions.'
        ]
      },
      {
        id: 'warranty',
        number: 7,
        heading: 'Workmanship & Manufacturer Warranties',
        paragraphs: [
          'Tropical Marine Construction stands behind its workmanship according to the terms provided in the approved proposal, invoice, or service agreement.',
          'Manufacturer warranties may apply to certain materials, equipment, boat lifts, components, or accessories. Warranty coverage may vary by manufacturer and product type.',
          'Warranties do not cover misuse, neglect, improper maintenance, storm damage, acts of nature, unauthorized modifications, or damage caused by third parties.'
        ]
      },
      {
        id: 'liability',
        number: 8,
        heading: 'Limitation of Liability',
        paragraphs: [
          'To the fullest extent permitted by law, Tropical Marine Construction is not liable for indirect, incidental, special, or consequential damages arising from use of our website, delays, project changes, or services performed.',
          'Our liability is limited to the amount paid for the specific service or project giving rise to the claim, unless otherwise required by law.'
        ]
      },
      {
        id: 'governing-law',
        number: 9,
        heading: 'Governing Law',
        paragraphs: [
          'These Terms and Conditions are governed by the laws of the State of Florida.',
          'Any disputes arising from the use of our website, estimates, proposals, or services shall be handled in accordance with applicable Florida law.'
        ]
      }
    ],

    contactTitle: 'Questions About Our Terms',
    contactIntro: (info) => `For questions regarding our terms, policies, estimates, or services, please contact us: ${info.businessName || 'Tropical Marine Construction'} | ${info.address || '1222 SE 47th St Suite C1, Cape Coral, FL 33904'} | ${info.email || 'info@tropicalmarineconstruction.com'} | ${info.phone || ''}`,

    conversionSection: {
      title: 'Clear Terms. Reliable Marine Work.',
      subtitle: 'We believe every waterfront project should begin with clear expectations, honest communication, and dependable service.',
      bgImage: ASSETS.BOATLIFTS_1 || ASSETS.DOCKS_1 || '',
      bgColor: 'bg-gray-50',
      buttonText: 'Return Home'
    }
  },

  he: {
    seo: {
      title: 'Terms & Conditions | Tropical Marine Construction',
      description: 'Review the Terms and Conditions for Tropical Marine Construction.',
      keywords: 'Tropical Marine Construction terms'
    },

    heroSection: {
      image: ASSETS.DOCKS_1 || ASSETS.BOATLIFTS_1 || '',
      overlay: 'bg-black/50'
    },

    title: 'Terms & Conditions',
    effectiveDateLabel: 'Effective Date',
    effectiveDate: 'October 1, 2025',

    introParagraphs: [
      'Welcome to Tropical Marine Construction. By accessing our website, requesting an estimate, scheduling a service, approving a proposal, or using our services, you agree to these Terms and Conditions.'
    ],

    sections: [
      {
        id: 'estimates',
        number: 1,
        heading: 'Estimates & Proposals',
        paragraphs: [
          'All estimates are based on the information available at the time they are prepared. Final pricing may change due to site conditions, permitting, materials, labor needs, or customer-requested changes.'
        ]
      },
      {
        id: 'permits',
        number: 2,
        heading: 'Permits & Approvals',
        paragraphs: [
          'Marine construction projects may require city, county, state, federal, HOA, environmental, or other approvals before work can begin.'
        ]
      },
      {
        id: 'payments',
        number: 3,
        heading: 'Payments',
        paragraphs: [
          'Payment terms, deposits, progress payments, and final balances will be outlined in the approved proposal, invoice, or service agreement.'
        ]
      },
      {
        id: 'scheduling',
        number: 4,
        heading: 'Scheduling & Delays',
        paragraphs: [
          'Project timelines may vary due to weather, tides, inspections, permitting, material availability, equipment scheduling, and site conditions.'
        ]
      },
      {
        id: 'changes',
        number: 5,
        heading: 'Change Orders',
        paragraphs: [
          'Any changes to the approved scope of work may require a written change order and may result in additional costs or timeline adjustments.'
        ]
      },
      {
        id: 'warranty',
        number: 6,
        heading: 'Workmanship & Manufacturer Warranties',
        paragraphs: [
          'Warranty coverage may vary by proposal, product, manufacturer, and project type. Warranties do not cover misuse, neglect, storm damage, or unauthorized modifications.'
        ]
      },
      {
        id: 'liability',
        number: 7,
        heading: 'Limitation of Liability',
        paragraphs: [
          'To the fullest extent permitted by law, Tropical Marine Construction is not liable for indirect, incidental, special, or consequential damages.'
        ]
      },
      {
        id: 'governing-law',
        number: 8,
        heading: 'Governing Law',
        paragraphs: [
          'These Terms and Conditions are governed by the laws of the State of Florida.'
        ]
      }
    ],

    contactTitle: 'Questions About Our Terms',
    contactIntro: (info) => `For questions regarding our terms, please contact us: ${info.businessName || 'Tropical Marine Construction'} | ${info.address || '1222 SE 47th St Suite C1, Cape Coral, FL 33904'} | ${info.email || 'info@tropicalmarineconstruction.com'} | ${info.phone || ''}`,

    conversionSection: {
      title: 'Clear Terms. Reliable Marine Work.',
      subtitle: 'Every waterfront project should begin with clear expectations and dependable communication.',
      bgImage: ASSETS.BOATLIFTS_1 || ASSETS.DOCKS_1 || '',
      bgColor: 'bg-gray-50',
      buttonText: 'Return Home'
    }
  }
};

export function getTermsStrings(language) {
  return termsData[language] || termsData.en;
}

export default termsData;